import '@caresend/types';
import { initWaypointAction, initElementTimestamp, objectKeys } from '@caresend/utils';
import 'lodash.isequal';
import { durationsByWaypointActionType } from '../model.js';
import { injectNullsDeep } from '../utils/injectNulls.js';
import 'firebase/auth';
import '../../../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../../../database/firebaseConfig/init.js';
import { dbUpdate } from '../../../../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../../ui/Icon.vue.js';
import '../../../../../../functions/configuration/icons.js';
import '../../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../../functions/tracking/index.js';
import '../../../../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';

/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-param-reassign */
const generateWaypointActions = (
/** Total duration of group of waypoint actions */
type, procedureIDs, waypointID, itineraryID, totalDuration) => {
    const actionDuration = totalDuration !== null && totalDuration !== void 0 ? totalDuration : durationsByWaypointActionType[type];
    let waypointActions = [];
    procedureIDs.forEach((procedureID) => {
        const waypointAction = initWaypointAction(type, waypointID);
        waypointAction.itineraryID = itineraryID;
        waypointAction.procedures = {
            ...waypointAction.procedures,
            [procedureID]: initElementTimestamp(procedureID),
        };
        waypointActions.push(waypointAction);
    });
    const distributedDuration = actionDuration / waypointActions.length;
    waypointActions = waypointActions.map((wpa) => ({
        ...wpa,
        duration: {
            ...wpa.duration,
            actionDuration: distributedDuration,
        },
    }));
    return waypointActions;
};
/** idk about all that up there but everything down here is def being used ⬇️⬇️⬇️⬇️⬇️ */
/**
 * Clean up db updates for firebase support by converting arrays to objects
 * and injecting nulls for empty strings, undefined, and empty objects/arrays.
 *
 * Conversion of arrays to objects helps minimize the size of the updates
 * by defining the explicit changes required to be applied to the database
 * each of which can be filtered out from the updates if there was no change.
 */
const cleanDbUpdates = (updates) => {
    const cleanUpdates = {};
    updates.forEach(([key, value]) => {
        cleanUpdates[key] = injectNullsDeep(value);
    });
    /**
     * Firebase rejects update objects which are provided as more specific than a parent path
     * This loop removes paths that are more specific than a parent path
     */
    objectKeys(cleanUpdates).forEach((path) => {
        const pathSplit = path.split('/');
        let pathSubstr = '';
        pathSplit.forEach((pathPart) => {
            pathSubstr += `${pathPart}`;
            if (pathSubstr === path)
                return;
            if (pathSubstr in cleanUpdates) {
                delete cleanUpdates[path];
            }
            pathSubstr += '/';
        });
    });
    return Object.entries(cleanUpdates);
};
/**
 * Example input:
 * [
 *  ['waypointActions/0/procedures/0', { id: '123', timestamp: 123 }],
 *  ['waypointActions/0/procedures/1', { id: '456', timestamp: 456 }],
 *  ['waypointActions/0/procedures/1', { timestamp: 789 }],
 * ]
 *
 * Example output:
 * {
 *  waypointActions: {
 *    0: {
 *      procedures: {
 *        0: { id: '123', timestamp: 123 },
 *        1: { id: '456', timestamp: 789 },
 *      },
 *    },
 *  },
 * }
 */
const groupUpdatesByMessyPathDefinitions = (uniquePathDefs) => {
    const updates = {};
    uniquePathDefs.forEach(([key, val]) => {
        /**
         * If a target value was explicitly defined as null,
         * it was indicated that the value should be deleted from the database.
         * This would otherwise be ignored when grouping updates.
         */
        if (val === null) {
            updates[key] = null;
            return;
        }
        const parentRefs = key.split('/');
        const maxIndex = parentRefs.length - 1;
        let currentKeyIndex = 0;
        let currentKey;
        let currentObjChild = updates;
        while (currentKeyIndex < maxIndex) {
            currentKey = parentRefs[currentKeyIndex];
            if (!currentKey)
                return;
            if (!currentObjChild[currentKey])
                currentObjChild[currentKey] = {};
            currentObjChild = currentObjChild[currentKey];
            currentKeyIndex += 1;
        }
        const nestedKey = parentRefs[maxIndex];
        if (!nestedKey)
            return;
        /** This writes to the allUpdates object since the currentObjChild is a reference to it */
        currentObjChild[nestedKey] = val !== null && val !== void 0 ? val : null;
    });
    return updates;
};
/**
 * Example input:
 * {
 *  waypointActions: {
 *    0: {
 *      procedures: {
 *        0: { id: '123', timestamp: 123 },
 *        1: { id: '456', timestamp: 456 },
 *      },
 *    },
 *  },
 * }
 *
 * Example output:
 * [
 *  ['waypointActions/0/procedures/0/id', '123'],
 *  ['waypointActions/0/procedures/0/timestamp', 123],
 *  ['waypointActions/0/procedures/1/id', '456'],
 *  ['waypointActions/0/procedures/1/timestamp', 456],
 * ]
 */
const flattenUpdatesToPathDefinitions = (updates, prefix = '') => {
    let result = [];
    for (const [key, value] of Object.entries(updates)) {
        const currentPath = prefix ? `${prefix}/${key}` : key;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            result = result.concat(flattenUpdatesToPathDefinitions(value, currentPath));
        }
        else {
            // Base case: add the current path and value to result
            result.push([currentPath, value]);
        }
    }
    return result;
};
const cleanAndUpdate = async (allUpdates) => {
    const cleanUpdates = cleanDbUpdates(allUpdates);
    /**
     * This methodology of organizing and grouping updates allows
     * for more transparent updates and sanitary updating. Lets say multiple updates
     * are registered where one node is updated with an object but another node
     * is defined as a child of the original node but with a conflicting value.
     */
    const groupedUpdates = groupUpdatesByMessyPathDefinitions(cleanUpdates);
    const flattenedUpdates = flattenUpdatesToPathDefinitions(groupedUpdates);
    const updates = Object.fromEntries(flattenedUpdates);
    console.info('Writing updates to rtdb...', updates);
    try {
        await dbUpdate('/', updates);
    }
    catch (error) {
        console.error('Error updating: ', error);
    }
};
const cleanInputWithInitializerDeep = (input, initializer) => {
    const result = { ...input };
    const keys = new Set([...Object.keys(input), ...Object.keys(initializer)]);
    keys.forEach((_key) => {
        const key = _key;
        const inputVal = input[key];
        const initVal = initializer[key];
        if (initVal !== null
            && typeof initVal === 'object'
            && !Array.isArray(initVal)) {
            result[key] = cleanInputWithInitializerDeep(inputVal !== null && inputVal !== void 0 ? inputVal : {}, initVal);
        }
        else {
            result[key] = inputVal !== undefined ? inputVal : initVal;
        }
    });
    return result;
};
/**
 * Helpers that allows store managers to accept either a target object or its ID
 * so hypothetical values can also be plugged into the computational helpers.
 */
const extractTarget = (targetOrID, getter) => {
    var _a;
    if (typeof targetOrID === 'string')
        return (_a = getter(targetOrID)) !== null && _a !== void 0 ? _a : undefined;
    return targetOrID;
};
/**
 * Helper to extract a target object from a getter with priority to an object
 * for cases where the target is known to exist.
 */
const extractTargetOrPriority = (targetOrID, getter, priority) => {
    var _a;
    if (typeof targetOrID === 'string') {
        if (targetOrID === priority.id)
            return priority;
        return (_a = getter(targetOrID)) !== null && _a !== void 0 ? _a : priority;
    }
    return targetOrID !== null && targetOrID !== void 0 ? targetOrID : priority;
};

export { cleanAndUpdate, cleanDbUpdates, cleanInputWithInitializerDeep, extractTarget, extractTargetOrPriority, flattenUpdatesToPathDefinitions, generateWaypointActions };
